import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { customersAPI, customerAISettingsAPI } from '../services/api';
import { format } from 'date-fns';

const tierColors = {
  standard: { bg: '#f3f4f6', color: '#374151' },
  premium: { bg: '#dbeafe', color: '#1d4ed8' },
  vip: { bg: '#fef3c7', color: '#b45309' },
  enterprise: { bg: '#ede9fe', color: '#6d28d9' }
};

const aiModeOptions = [
  { value: 'both', label: 'Both Options', description: 'Customer can use embedded keys or BYOK' },
  { value: 'embedded_only', label: 'Embedded Only', description: 'Uses shared pool keys (no API key config shown)' },
  { value: 'byok_only', label: 'BYOK Only', description: 'Customer must provide their own API key' },
  { value: 'disabled', label: 'Disabled', description: 'AI features completely disabled' },
];

const CustomerDetail = () => {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [aiSettings, setAiSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingAI, setSavingAI] = useState(false);

  useEffect(() => {
    fetchCustomer();
  }, [id]);

  const fetchCustomer = async () => {
    try {
      const [customerRes, licensesRes, aiRes] = await Promise.all([
        customersAPI.get(id),
        customersAPI.getLicenses(id),
        customerAISettingsAPI.get(id).catch(() => ({ data: null }))
      ]);
      setCustomer(customerRes.data);
      setLicenses(licensesRes.data);
      setAiSettings(aiRes.data);
    } catch (err) {
      console.error('Failed to fetch customer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAIModeChange = async (newMode) => {
    setSavingAI(true);
    try {
      const res = await customerAISettingsAPI.update(id, { ai_mode: newMode });
      setAiSettings(res.data);
    } catch (err) {
      console.error('Failed to update AI settings:', err);
      alert('Failed to update AI settings');
    } finally {
      setSavingAI(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  if (!customer) {
    return <div className="error-container"><p>Customer not found</p></div>;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{customer.company_name}</h1>
          <p className="page-description">{customer.contact_name} - {customer.email}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Link to={`/dashboard/licenses/create?customer=${id}`} className="btn btn-primary">
            Create License
          </Link>
          <button className="btn btn-secondary">Edit</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Customer Info */}
          <div className="card">
            <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>Customer Details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Status</span>
                <p><span className={`badge badge-${customer.status === 'active' ? 'success' : 'gray'}`}>{customer.status}</span></p>
              </div>
              <div>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Tier</span>
                <p>
                  <span
                    style={{
                      padding: '4px 12px',
                      borderRadius: '9999px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      backgroundColor: tierColors[customer.tier]?.bg || '#f3f4f6',
                      color: tierColors[customer.tier]?.color || '#374151'
                    }}
                  >
                    {customer.tier || 'standard'}
                  </span>
                </p>
              </div>
              <div>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Phone</span>
                <p>{customer.phone || 'N/A'}</p>
              </div>
              <div>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Address</span>
                <p>
                  {customer.address_line1 && <>{customer.address_line1}<br /></>}
                  {customer.city && <>{customer.city}, {customer.state} {customer.postal_code}<br /></>}
                  {customer.country}
                </p>
              </div>
              <div>
                <span style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>Created</span>
                <p>{format(new Date(customer.created_at), 'MMMM d, yyyy')}</p>
              </div>
            </div>
          </div>

          {/* AI Settings Card */}
          <div className="card">
            <h3 style={{ marginBottom: '1rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              AI Configuration
            </h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, fontSize: '0.9rem' }}>
                AI Mode
              </label>
              <select
                className="form-select"
                value={aiSettings?.ai_mode || 'both'}
                onChange={(e) => handleAIModeChange(e.target.value)}
                disabled={savingAI}
                style={{ width: '100%' }}
              >
                {aiModeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {savingAI && <small style={{ color: 'var(--primary)' }}>Saving...</small>}
            </div>

            <div style={{ background: '#f9fafb', padding: '0.75rem', borderRadius: '6px', fontSize: '0.85rem' }}>
              <div style={{ marginBottom: '0.5rem' }}>
                <strong>Current Mode:</strong>{' '}
                {aiModeOptions.find(o => o.value === (aiSettings?.ai_mode || 'both'))?.label}
              </div>
              <div style={{ color: 'var(--gray-500)' }}>
                {aiModeOptions.find(o => o.value === (aiSettings?.ai_mode || 'both'))?.description}
              </div>
            </div>

            {aiSettings && (
              <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', fontSize: '0.85rem' }}>
                <div>
                  <span style={{ color: 'var(--gray-500)' }}>BYOK Allowed:</span>{' '}
                  <span className={`badge badge-${aiSettings.allows_byok ? 'success' : 'gray'}`}>
                    {aiSettings.allows_byok ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span style={{ color: 'var(--gray-500)' }}>Embedded Keys:</span>{' '}
                  <span className={`badge badge-${aiSettings.allows_embedded ? 'success' : 'gray'}`}>
                    {aiSettings.allows_embedded ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Licenses */}
        <div className="card">
          <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>
            Licenses ({licenses.length})
          </h3>
          {licenses.length === 0 ? (
            <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem' }}>
              No licenses yet
            </p>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Expiry</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license) => (
                    <tr key={license.id}>
                      <td style={{ textTransform: 'capitalize' }}>{license.license_type}</td>
                      <td>
                        <span className={`badge badge-${license.is_expired ? 'danger' : license.status === 'active' ? 'success' : 'warning'}`}>
                          {license.is_expired ? 'Expired' : license.status}
                        </span>
                      </td>
                      <td>{format(new Date(license.expiry_date), 'MMM d, yyyy')}</td>
                      <td>
                        <Link to={`/dashboard/licenses/${license.id}`} className="btn btn-sm btn-secondary">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetail;
